import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Button,
    Text,
    Flex,
    Input,
    SearchIcon,
    Popup,
    List,
    ChevronDownIcon,
    CloseIcon,
    Loader
} from "@fluentui/react-northstar";

const MappingSelector = React.memo(({ onUserSelect, selectedContactId, options, loading, assignedContactIds }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // --- DEBOUNCE: Search query ---
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const searchInputRef = useRef(null);

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 400);
        return () => clearTimeout(timerId);
    }, [searchQuery]);

    const selectedUser = useMemo(() => {
        if (!selectedContactId) return null;
        return options.find(opt => opt.data && String(opt.data.id) === String(selectedContactId));
    }, [selectedContactId, options]);

    const filteredOptions = useMemo(() => {
        const assignedSet = new Set(Array.from(assignedContactIds).map(String));
        const currentSelectedIdStr = selectedContactId ? String(selectedContactId) : "";

        return options.filter(item => {
            if (!item.data) return false;

            const itemIdStr = String(item.data.id || "");

            const isAssignedToOther = assignedSet.has(itemIdStr) && itemIdStr !== currentSelectedIdStr;
            if (isAssignedToOther) return false;

            if (!debouncedSearchQuery) return true;
            const lowerQuery = debouncedSearchQuery.toLowerCase();
            const name = (item.data.name || "").toLowerCase();
            const email = (item.data.email || "").toLowerCase();

            return name.includes(lowerQuery) || email.includes(lowerQuery) || itemIdStr.includes(lowerQuery);
        });
    }, [options, debouncedSearchQuery, assignedContactIds, selectedContactId]);

    const handleSelect = (userItem) => {
        if (userItem && userItem.data) {
            onUserSelect(userItem.data.id);
        }
        setIsOpen(false);
        setSearchQuery("");
        setDebouncedSearchQuery("");
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onUserSelect("");
        setIsOpen(false);
        setSearchQuery("");
        setDebouncedSearchQuery("");
    };

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => {
                if(searchInputRef.current) searchInputRef.current.focus();
            }, 50);
        }
    }, [isOpen]);

    const listItems = filteredOptions.map(opt => {
        const isSelected = String(opt.data.id) === String(selectedContactId);

        return {
            key: opt.data.id,
            content: (
                <Flex column style={{ padding: '8px 10px', borderBottom: '1px solid #f3f2f1' }}>
                    <Text content={opt.data.name} weight="bold" size="medium" />
                    <Flex gap="gap.small">
                        <Text content={`ID: ${opt.data.id}`} size="small" style={{ color: '#6264a7' }} />
                        <Text content="|" size="small" style={{ color: '#ccc' }} />
                        <Text content={opt.data.email} size="small" style={{ color: '#666' }} />
                    </Flex>
                </Flex>
            ),
            onClick: () => handleSelect(opt),
            styles: {
                cursor: 'pointer',
                padding: 0,
                backgroundColor: isSelected ? '#ede7f6' : 'transparent',
                ':hover': {
                    backgroundColor: isSelected ? '#ede7f6' : '#f0f0f8'
                }
            }
        };
    });

    const COMPONENT_WIDTH = '260px';

    return (
        <Popup
            inline
            open={isOpen}
            onOpenChange={(e, { open }) => setIsOpen(open)}
            align="start"
            position="below"
            offset={[0, 0]}
            content={{
                styles: {
                    width: COMPONENT_WIDTH,
                    padding: '0',
                    maxHeight: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #d1d1d1',
                    borderTop: 'none',
                    boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                    zIndex: 1000
                },
                content: (
                    <>
                        <div style={{ padding: '8px', borderBottom: '1px solid #e1dfdd', backgroundColor: '#fff' }}>
                            <Input
                                fluid
                                icon={<SearchIcon />}
                                placeholder="Benutzer suchen..."
                                value={searchQuery}
                                onChange={(e, { value }) => setSearchQuery(value)}
                                ref={searchInputRef}
                            />
                        </div>

                        <div style={{ overflowY: 'auto', flex: 1, maxHeight: '250px', backgroundColor: '#fff' }}>
                            {loading ? (
                                <Flex hAlign="center" padding="padding.medium"><Loader size="small" /></Flex>
                            ) : listItems.length > 0 ? (
                                <List selectable items={listItems} />
                            ) : (
                                <Text
                                    content="Kein Benutzer gefunden"
                                    style={{ padding: '15px', color: '#999', display: 'block', textAlign: 'center' }}
                                />
                            )}
                        </div>
                    </>
                )
            }}
            trigger={
                <Button
                    fluid
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                        width: COMPONENT_WIDTH,
                        textAlign: 'left',
                        justifyContent: 'space-between',
                        fontWeight: 'normal',
                        backgroundColor: '#fff',
                        border: '1px solid #d1d1d1',
                        borderBottom: isOpen ? '1px solid transparent' : '1px solid #d1d1d1',
                        borderRadius: isOpen ? '3px 3px 0 0' : '3px',
                        paddingLeft: '10px',
                        paddingRight: '10px',
                        height: '32px'
                    }}
                >
                    <Flex vAlign="center" style={{ overflow: 'hidden', width: '100%' }}>
                        <Text
                            content={selectedUser ? `${selectedUser.data.name} (${selectedUser.data.id})` : "Benutzer auswählen oder suchen..."}
                            truncate
                            style={{ flex: 1, color: selectedUser ? '#252423' : '#605e5c' }}
                        />
                    </Flex>
                    <Flex vAlign="center" shrink={0}>
                        {selectedUser ? (
                            <CloseIcon
                                size="small"
                                onClick={handleClear}
                                style={{ marginRight: '8px', cursor: 'pointer', color: '#605e5c' }}
                                title="Auswahl löschen"
                            />
                        ) : null}
                        <ChevronDownIcon size="small" style={{ color: '#605e5c' }} />
                    </Flex>
                </Button>
            }
        />
    );
});

export default MappingSelector;
